import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useIsMobile, useSidebarShowSubItems } from '../../hooks';
import { Avatar } from '../Avatar';
import { DropdownMenuItem } from '../DropdownMenu';
import { PopoverMenu } from '../PopoverMenu';
import Header from './Header';
import { MenuBlock } from './MenuBlock';
import { AppSelectorInfo, SidebarMenuItem, User } from './types';

export * from './types';

export interface SidebarProps {
  menuItems: SidebarMenuItem[];
  avatarUrl?: string;
  label?: ReactNode;
  profileMenuItems?: DropdownMenuItem[];
  profileHeaderMenu?: JSX.Element;
  headerMenuItems?: DropdownMenuItem[];
  isHeaderMenuItemsLoading?: boolean;
  headerMenuSelectedItem?: DropdownMenuItem & { labelCurrent?: ReactNode };
  disabledHeaderMenu?: boolean;
  minimizable?: boolean;
  user?: User;
  role?: string;
  entity?: {
    type?: string;
  };
  compliance?: boolean;
  appSelectorInfo?: AppSelectorInfo;
  searchField?: boolean;
}

export const Sidebar = ({
  user,
  entity,
  avatarUrl,
  menuItems,
  headerMenuItems,
  profileMenuItems,
  profileHeaderMenu,
  disabledHeaderMenu,
  headerMenuSelectedItem,
  isHeaderMenuItemsLoading,
  appSelectorInfo,
  searchField,
  label = 'Compli One',
  minimizable = true,
  compliance = false,
  role,
}: SidebarProps) => {
  const { isMobile } = useIsMobile();
  const [isOpened, setIsOpened] = useState(!isMobile && window.screen.width > 1024);
  const [search, setSearch] = useState('');
  const auth = useAuth();

  const { openAll, closeAll, sessionId, setSessionId } = useSidebarShowSubItems();

  useEffect(() => {
    if (sessionId !== auth?.user?.session_state) {
      closeAll();
      setSessionId(auth?.user?.session_state || null);
    }
  }, [auth?.user?.session_state, closeAll, sessionId, setSessionId]);

  const handleChangeOpen = () => {
    setIsOpened(open => !open);
  };

  const [resolveItems, resolveItemsCount] = useMemo(() => {
    let count = 0;

    if (!search) return [menuItems, count];

    const items = menuItems
      .filter(({ label, subItems = [], searchKeywords = [] }) => {
        const doesMatchMainLabel =
          typeof label === 'string' && label.toLowerCase().includes(search.toLowerCase());
        const doesMatchSubItemsLabel = subItems.some(
          ({ label }) =>
            typeof label === 'string' && label.toLowerCase().includes(search.toLowerCase()),
        );

        const doesMatchMainSearchKeywords = searchKeywords.some(keyWord =>
          keyWord.toLowerCase().includes(search.toLowerCase()),
        );

        const doesMatchSubItemsSearchKeywords = subItems
          .flatMap(({ searchKeywords = [] }) => searchKeywords)
          .find(keyWord => keyWord.toLowerCase() === search.toLowerCase());

        if (doesMatchMainLabel || doesMatchMainSearchKeywords) {
          count++;
        }

        return (
          doesMatchMainLabel ||
          doesMatchSubItemsLabel ||
          doesMatchMainSearchKeywords ||
          doesMatchSubItemsSearchKeywords
        );
      })
      .map(item => ({
        ...item,
        subItems:
          item.subItems &&
          item.subItems.filter(({ label, searchKeywords = [] }) => {
            const doesLabelMatch =
              typeof label === 'string' && label.toLowerCase().includes(search.toLowerCase());
            const doesMatchMainSearchKeywords = searchKeywords.some(keyWord =>
              keyWord.toLowerCase().includes(search.toLowerCase()),
            );

            if (doesLabelMatch || doesMatchMainSearchKeywords) {
              count++;
            }
            return doesLabelMatch || doesMatchMainSearchKeywords;
          }),
      }));

    return [items, count];
  }, [menuItems, search]);

  const handleChangeSearch = useCallback(
    (value: string) => {
      setSearch(oldValue => {
        if (!oldValue && value) openAll();

        if (oldValue && !value) closeAll();
        return value;
      });
    },
    [closeAll, openAll],
  );

  return (
    <aside
      className={classNames(
        'h-screen sm:flex sm:flex-col sm:items-start sm:justify-start bg-gray-900 text-white transition-all hidden',
        {
          'min-w-[240px] w-[240px]': isOpened && !isMobile,
          'min-w-full': isOpened && isMobile,
          'min-w-[72px] w-[72px]': !isOpened,
        },
      )}
    >
      <Header
        isOpened={isOpened}
        label={label}
        appSelectorInfo={appSelectorInfo}
        minimizable={minimizable}
        handleChangeOpen={handleChangeOpen}
        headerMenuItems={headerMenuItems}
        headerMenuSelectedItem={headerMenuSelectedItem}
        resolveItemsCount={resolveItemsCount}
        isHeaderMenuItemsLoading={isHeaderMenuItemsLoading}
        disabledHeaderMenu={disabledHeaderMenu}
        searchField={searchField}
        search={search}
        setSearch={handleChangeSearch}
      />

      <MenuBlock
        menuItems={resolveItems}
        isOpened={isOpened}
        isSearchUsed={!!search}
        search={search}
      />

      <PopoverMenu
        items={profileMenuItems || []}
        headerMenu={profileHeaderMenu}
        placement="top-start"
        classes={{ panel: 'p-0 rounded min-w-[200px] bg-white z-[2] border border-gray-200' }}
        button={
          <div className="w-full flex items-center px-6 pb-6 space-x-3">
            <Avatar src={avatarUrl} firstName={user?.first_name} lastName={user?.last_name} />
            {isOpened && (
              <>
                <div className="flex flex-col items-start capitalize">
                  {compliance ? (
                    <div className="flex flex-col items-start space-y-1">
                      <div className="text-[14px] leading-[22px]">
                        {user?.first_name} {user?.last_name}
                      </div>
                      <div className="w-full flex items-center space-x-1">
                        {/*<p className="text-[11px] leading-[13px] opacity-64">{user?.role}</p>*/}
                        <div className="text-[11px] leading-[13px] opacity-64 text-start">
                          {role || user?.business?.name}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-start">
                      <div className="text-[14px] leading-[22px] text-left">
                        {user?.first_name} {user?.last_name}
                      </div>
                      <div className="flex justify-end text-left">
                        <div className="text-[12px] leading-[20px] opacity-64">
                          {role || entity?.type}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        }
      />
    </aside>
  );
};
